import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, mediaUrl } from "../../lib/api";
import { RichEditor } from "../RichEditor";
import { ArrowLeft, Save, Globe, Loader2, ExternalLink } from "lucide-react";

const slugify = (s) =>
  s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-");

const DOC_STATUS_OPTIONS = [
  { value: "active", label: "— None —" },
  { value: "new", label: "New" },
  { value: "rewritten", label: "Rewritten" },
  { value: "refreshed", label: "Refreshed" },
];

export default function AdminDocEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({
    section_slug: "",
    slug: "",
    title: "",
    description: "",
    content_html: "",
    status: "active",
    redirects_from_text: "",
    order: 99,
    is_published: true,
  });
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [imgUploading, setImgUploading] = useState(false);
  const slugTouched = useRef(false);
  const editorRef = useRef(null);

  // Load section list
  useEffect(() => {
    api.get("/admin/docs").then((r) => {
      const list = r.data.sections || [];
      setSections(list);
      if (isNew && !form.section_slug && list[0]) {
        setForm((p) => ({ ...p, section_slug: list[0].slug }));
      }
    });
  }, []);

  // Load page when editing
  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/docs/pages/${id}`).then((r) => {
      const d = r.data;
      setForm({
        section_slug: d.section_slug || "",
        slug: d.slug || "",
        title: d.title || "",
        description: d.description || "",
        content_html: d.content_html || "",
        status: d.status || "active",
        redirects_from_text: (d.redirects_from || []).join("\n"),
        order: typeof d.order === "number" ? d.order : 99,
        is_published: d.is_published !== false,
      });
      slugTouched.current = true;
      setLoaded(true);
    }).catch(() => { setError("Page not found"); setLoaded(true); });
  }, [id, isNew]);

  const onTitleChange = (e) => {
    const v = e.target.value;
    setForm((p) => ({ ...p, title: v, slug: slugTouched.current ? p.slug : slugify(v) }));
  };

  const onInsertImage = async () => {
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return resolve(null);
        setImgUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("folder", "docs/inline");
          const r = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          resolve(mediaUrl(r.data.media_id));
        } catch (_) { resolve(null); }
        finally { setImgUploading(false); }
      };
      input.click();
    });
  };

  const save = async (publishOverride = null) => {
    setError("");
    if (!form.title.trim()) { setError("Title is required"); return; }
    if (!form.section_slug) { setError("Pick a section"); return; }
    if (!form.slug.trim()) { setError("Slug is required"); return; }

    const payload = {
      section_slug: form.section_slug,
      slug: slugify(form.slug),
      title: form.title.trim(),
      description: form.description.trim(),
      content_html: form.content_html,
      status: form.status,
      redirects_from: form.redirects_from_text
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      order: Number(form.order) || 99,
      is_published: publishOverride === null ? form.is_published : publishOverride,
    };

    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post("/admin/docs/pages", payload);
        navigate(`/admin/docs/${r.data.page_id}`, { replace: true });
      } else {
        const r = await api.put(`/admin/docs/pages/${id}`, payload);
        setForm((p) => ({ ...p, is_published: r.data.is_published !== false }));
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!loaded) return <p className="text-aubergine/55 font-mono text-xs">Loading…</p>;

  const previewHref = !isNew && form.is_published ? `/docs/${form.section_slug}/${form.slug}` : null;

  return (
    <div data-testid="admin-doc-editor">
      <button onClick={() => navigate("/admin/docs")} data-testid="doc-editor-back" className="inline-flex items-center text-aubergine/65 hover:text-aubergine font-mono text-[11px] uppercase tracking-[0.15em] mb-6">
        <ArrowLeft className="mr-2 h-3.5 w-3.5" /> All docs
      </button>

      <header className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">{isNew ? "New docs page" : "Edit docs page"}</p>
          <h1 className="font-serif text-3xl text-aubergine">{form.title || "Untitled"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider ${form.is_published ? "bg-champagne/15 text-aubergine" : "bg-aubergine/10 text-aubergine/65"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${form.is_published ? "bg-champagne" : "bg-aubergine/40"}`} />
            {form.is_published ? "Published" : "Draft"}
          </span>
          {previewHref && (
            <a href={previewHref} target="_blank" rel="noreferrer" data-testid="doc-editor-preview" className="inline-flex items-center gap-1.5 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-xs">
              <ExternalLink className="h-3.5 w-3.5" /> Preview
            </a>
          )}
          <button onClick={() => save(false)} disabled={saving} data-testid="doc-editor-save-draft" className="inline-flex items-center gap-2 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-xs disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save draft
          </button>
          <button onClick={() => save(true)} disabled={saving} data-testid="doc-editor-publish" className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-3 py-2 font-mono text-xs disabled:opacity-60">
            <Globe className="h-3.5 w-3.5" />
            {form.is_published ? "Update" : "Publish"}
          </button>
        </div>
      </header>

      {error && <div className="mb-4 rounded-md border border-signal-red/40 bg-signal-red/10 px-4 py-2 text-sm text-signal-red" data-testid="doc-editor-error">{error}</div>}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          <Field label="Title">
            <input data-testid="doc-editor-input-title" value={form.title} onChange={onTitleChange} className={inputCls} placeholder="Page title" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Section">
              <select
                data-testid="doc-editor-input-section"
                value={form.section_slug}
                onChange={(e) => setForm((p) => ({ ...p, section_slug: e.target.value }))}
                className={inputCls}
              >
                {sections.map((s) => (
                  <option key={s.slug} value={s.slug}>{s.title}</option>
                ))}
              </select>
            </Field>
            <Field label="Slug" hint={`URL: /docs/${form.section_slug || "section"}/${form.slug || "page-slug"}`}>
              <input
                data-testid="doc-editor-input-slug"
                value={form.slug}
                onChange={(e) => { slugTouched.current = true; setForm((p) => ({ ...p, slug: slugify(e.target.value) })); }}
                className={`${inputCls} font-mono text-xs`}
                placeholder="page-slug"
              />
            </Field>
          </div>
          <Field label="Description" hint="Short summary shown under the title.">
            <textarea data-testid="doc-editor-input-description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className={`${inputCls} resize-none`} rows={2} />
          </Field>
          <Field label="Content">
            {imgUploading && <p className="mb-2 text-xs text-aubergine/65 inline-flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Uploading image…</p>}
            <RichEditor
              value={form.content_html}
              onChange={(html) => setForm((p) => ({ ...p, content_html: html }))}
              onInsertImage={onInsertImage}
              editorRef={editorRef}
            />
          </Field>
        </div>

        <aside className="space-y-5">
          <div className="rounded-lg border border-aubergine/10 bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">Visibility</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm((p) => ({ ...p, is_published: e.target.checked }))}
                data-testid="doc-editor-input-published"
              />
              <span className="text-sm text-aubergine">Published</span>
            </label>
            <p className="mt-2 text-[11px] text-aubergine/55">Drafts are hidden from <span className="font-mono">/docs</span> and from search.</p>
          </div>

          <div className="rounded-lg border border-aubergine/10 bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">Badge</p>
            <Field label="Status">
              <select
                data-testid="doc-editor-input-status"
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                className={inputCls}
              >
                {DOC_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-aubergine/55">Only <span className="font-mono">New</span> is shown publicly. Others are admin-only.</p>
            </Field>
            <Field label="Order">
              <input
                type="number"
                data-testid="doc-editor-input-order"
                value={form.order}
                onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
                className={inputCls}
              />
              <p className="mt-1 text-[11px] text-aubergine/55">Lower numbers appear first in the section.</p>
            </Field>
          </div>

          <div className="rounded-lg border border-aubergine/10 bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">Redirects</p>
            <Field label="Redirects from" hint="One per line. Docs-relative paths (e.g. /introduction/t-rex).">
              <textarea
                data-testid="doc-editor-input-redirects"
                value={form.redirects_from_text}
                onChange={(e) => setForm((p) => ({ ...p, redirects_from_text: e.target.value }))}
                className={`${inputCls} font-mono text-xs resize-none`}
                rows={4}
                placeholder="/introduction/t-rex"
              />
            </Field>
          </div>
        </aside>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-aubergine/15 bg-white px-3 py-2 text-sm outline-none focus:border-champagne transition-colors";

function Field({ label, hint, children }) {
  return (
    <div className="mb-3 last:mb-0">
      <label className="block text-xs font-medium text-aubergine mb-1.5">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-aubergine/55">{hint}</p>}
    </div>
  );
}
